import React, { useState } from 'react';
import { cancelarPropuestaReparto, enviarAprobacionReparto } from '../api/comisiones';

const ESTADO = {
  aprobada: { label: 'Aprobó', cls: 'text-emerald-700' },
  rechazada: { label: 'Rechazó', cls: 'text-red-600' },
  pendiente: { label: 'Falta', cls: 'text-amber-700' },
};

/**
 * Whether the correo for a pending propuesta actually left (functions stamps
 * `envio` after each attempt). Null when there is nothing to say.
 * @returns {{label: string, cls: string, error?: string}|null}
 */
export function estadoEnvio(propuesta) {
  if (!propuesta || propuesta.estado !== 'pendiente') return null;
  const e = propuesta.envio;
  if (!e || !e.estado) {
    return { label: 'Enviando correo', cls: 'bg-zinc-100 text-zinc-600' };
  }
  if (e.estado === 'fallido') {
    return { label: 'Correo sin enviar', cls: 'bg-red-100 text-red-700', error: e.error || null };
  }
  if (e.estado === 'parcial') {
    return { label: 'Correo parcial', cls: 'bg-amber-100 text-amber-800', error: e.error || null };
  }
  return null;
}

/** The pending propuesta for a cliente or a fianza, or null. */
export function propuestaPara(propuestas, { clienteId, fianzaId }) {
  return (propuestas || []).find((p) => {
    const o = p.objetivo || {};
    if (fianzaId) return o.tipo === 'fianza' && o.fianzaId === fianzaId;
    return o.tipo === 'cliente' && o.clienteId === clienteId;
  }) || null;
}

/**
 * Banner for a reparto change waiting on the socios: what was proposed, who
 * answered, and the two owner actions (re-send the email, cancel).
 */
export function PropuestaRepartoPendiente({ propuesta, compact = false }) {
  const [busy, setBusy] = useState(null);
  const [aviso, setAviso] = useState(null);
  if (!propuesta) return null;

  const aprobadores = Object.entries(propuesta.aprobadores || {});
  const envio = estadoEnvio(propuesta);
  const reparto = (propuesta.reparto || [])
    .filter((r) => r.pct > 0)
    .map((r) => `${r.nombre || r.asociadoId} ${r.pct}%`)
    .join(', ');

  const reenviar = async () => {
    setBusy('reenviar');
    setAviso(null);
    try {
      const r = await enviarAprobacionReparto(propuesta.id);
      setAviso(r?.omitidos
        ? 'El correo se envió hace un momento; espera un par de minutos para reenviarlo.'
        : `Correo enviado a ${r.sent.length} socio${r.sent.length !== 1 ? 's' : ''}.`);
    } catch (e) {
      setAviso(e.message || 'No se pudo enviar el correo.');
    } finally {
      setBusy(null);
    }
  };

  const cancelar = async () => {
    setBusy('cancelar');
    setAviso(null);
    try {
      await cancelarPropuestaReparto(propuesta.id);
    } catch (e) {
      setAviso(e.message || 'No se pudo cancelar la propuesta.');
      setBusy(null);
    }
  };

  return (
    <div className={`rounded-xl border border-amber-200 bg-amber-50 ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            Pendiente de aprobación
          </span>
          {envio && (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${envio.cls}`}
              title={envio.error || undefined}
            >
              {envio.label}
            </span>
          )}
        </span>
        <div className="flex gap-3 text-xs">
          <button type="button" disabled={!!busy} onClick={reenviar}
            className="font-medium text-azul-surelia hover:underline disabled:opacity-50">
            {busy === 'reenviar' ? 'Enviando…' : 'Reenviar correo'}
          </button>
          <button type="button" disabled={!!busy} onClick={cancelar}
            className="font-medium text-gray-500 hover:underline disabled:opacity-50">
            {busy === 'cancelar' ? 'Cancelando…' : 'Cancelar propuesta'}
          </button>
        </div>
      </div>
      <div className="mt-2 text-sm text-gray-800">
        {reparto || (propuesta.objetivo?.tipo === 'fianza' ? 'Regresar al reparto del cliente' : 'Sin reparto')}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
        {aprobadores.map(([id, a]) => (
          <span key={id} className="text-gray-600">
            {a.nombre}: <span className={`font-medium ${ESTADO[a.estado]?.cls || ''}`}>{ESTADO[a.estado]?.label || a.estado}</span>
          </span>
        ))}
        {propuesta.regla === 'uno' && <span className="text-gray-400">Basta una aprobación</span>}
      </div>
      {aviso && <div className="mt-1 text-xs text-gray-600">{aviso}</div>}
    </div>
  );
}

export default PropuestaRepartoPendiente;
